import React from 'react'
import DialogLayout, { DialogLayoutProps } from 'shared/components/Layout/Dialog'
import pick from 'lodash/pick'
import omit from 'lodash/omit'
import { hideDialog, dialogProcessing, minimizeDialog } from 'shared/redux/app/reducer'
import useForm, { UseFormParams, FormState, FormHandlers } from 'shared/hooks/useForm'
import getDisplayName from './getDisplayName'
import { useSelector, useDispatch } from 'react-redux'
import { GlobalState } from 'shared/redux/reducer'

const dialogProps = [
  'dialogId',
  'dialogActionsRenderer',
  'dialogTitleRenderer',
  'title',
  'subtitle',
  'dialogContainerClassName',
  'dialogClassName',
  'hasExtra',
  'hasCancel',
  'hasContinue',
  'titleClassName',
  'contentClassName',
  'footerClassName',
  'minimize',
  'context',
  'cancelLabel',
  'extraLabel',
  'isNewDesign',
  'continueLabel'
]

const formProps = ['initialFields', 'validator', 'customChangeHandler', 'onValid']

export type WithDialogProps<T> = Omit<DialogLayoutProps, 'onCancel'> &
  UseFormParams<T> & {
    continueIsDisabled?(form: T, props: any): boolean
    onCancel: (event?: React.MouseEvent<HTMLElement, MouseEvent>, fields?: T) => void
    onClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>, fields?: T) => void
  }

export type WrappedProps<T> = Pick<DialogLayoutProps, 'cancelLabel' | 'extraLabel' | 'continueLabel' | 'hasCancel' | 'hasContinue' | 'onCancel' | 'onContinue' | 'onClose' | 'onExtra' | 'isProcessing'> & {
  formState: FormState<T>
  formHandlers: FormHandlers,
}

export type DialogContentProps<T> = {
  formState: FormState<T>
  formHandlers: FormHandlers
}

export default () => {
  return function<T>(WrappedComponent: React.FC<WrappedProps<T>>) {
    function Dialog(props: WithDialogProps<T>) {
      const dispatch = useDispatch()
      const isProcessing = useSelector((state: GlobalState) => state.app.dialogProcessing)
      const role_level = useSelector((state: GlobalState) => state.auth.user?.role_level)
      const { initialFields } = props
      const [formState, formHandlers] = useForm<T>({ ...pick(props, formProps), onValid, initialFields })
      const { title } = props
      return (
        <DialogLayout
          {...pick(props, dialogProps)}
          title={title}
          onContinue={props.onContinue || onContinue}
          onCancel={onCancelWithData(formState.fields, 'cancel')}
          onExtra={props.onExtra || onExtra}
          onClose={onCancelWithData(formState.fields, 'close')}
          onMinimize={props.onMinimize || onMinimize}
          isProcessing={isProcessing}
          role_level={role_level}
          continueIsDisabled={continueIsDisabled()}
        >
          <WrappedComponent
            formState={formState}
            formHandlers={formHandlers}
            onContinue={onContinue}
            onExtra={onExtra}
            onCancel={onCancelWithData(formState.fields, 'cancel')}
            onClose={onCancelWithData(formState.fields, 'close')}
            isProcessing={isProcessing}
            {...({
              ...omit(props, dialogProps/*.concat(formProps)*/.concat(['onCancel', 'onClose'])),
              ...pick(props, ['cancelLabel', 'extraLabel', 'continueLabel', 'hasContinue', 'hasCancel']),
              pageTitle: title
            })}
          />
        </DialogLayout>
      )

      function continueIsDisabled() {
        const { continueIsDisabled = () => false } = props
        return continueIsDisabled(formState.fields, omit(props, dialogProps.concat(formProps).concat(['onCancel', 'onClose'])))
      }

      function onValid(arg: T) {
        dispatch(dialogProcessing(true))
        props.onValid && props.onValid(arg)
      }
      function onContinue() {
        formHandlers.onValidate()
      }
      function onExtra() {
        formHandlers.onValidate()
      }
      function onCancel() {
        dispatch(hideDialog())
      }

      function onCancelWithData(fields: T, type: string) {
        return (event?: React.MouseEvent<HTMLElement, MouseEvent>): void => {
          if (props.onCancel && type === 'cancel')
            return props.onCancel(event, fields)

          if (props.onClose && type === 'close')
            return props.onClose(event, fields)

          onCancel()
        }
      }

      function onMinimize() {
        dispatch(minimizeDialog(title))
      }
    }
    Dialog.displayName = `withDialog(${getDisplayName(WrappedComponent)})`
    return Dialog
  }
}
